import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { Injectable } from '@angular/core';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { environment } from 'environments/environment';
import { take } from 'rxjs/operators';
import { AppNavigationService } from './app-navigation.service';
import { AuthenticationService } from './authentication.service';
import { InstrumentationService } from './instrumentation.service';
import { SubscriptionService } from './subscription.service';

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private isFirstNewOrg = false;
  private isTrackingInitialised = false;
  private organisation: OrganisationDto | null = null;
  private sessionReplayTracking: any;

  get isNotTestOrganisation() {
    // eslint-disable-next-line ize/ize
    return !this.organisation?.name.includes('organisation');
  }

  constructor(
    private instrumentation: InstrumentationService,
    private authService: AuthenticationService,
    private appNavigationService: AppNavigationService,
    private subscriptionService: SubscriptionService,
  ) {
    this.appNavigationService.onOrganisationChanged.subscribe(() => {
      setTimeout(() => {
        const organisation = this.appNavigationService.getActiveOrganisation();
        this.updateIdentity(organisation);
      }, 1000);
    });
  }

  initaliseUserTracking(isFirstNewOrg = false) {
    if (this.isNotTestOrganisation && !this.isTrackingInitialised) {
      // '-' is the "intentionally unset" sentinel from the variable group —
      // amplitude.init with a bogus key would 400 every event, so just bail.
      const apiKey = environment.amplitude.apiKey;
      if (!apiKey || apiKey === '-') {
        return;
      }

      this.isFirstNewOrg = isFirstNewOrg;
      this.isTrackingInitialised = true;
      this.sessionReplayTracking = sessionReplayPlugin({ sampleRate: 1 });

      const userId = this.authService.getCurrentLoggedOnUserId();
      const userEmail = this.authService.getCurrentUserEmail();

      // amplitude.setUserId(userId);

      amplitude.init(apiKey, userEmail, {
        autocapture: {
          attribution: false,
          pageViews: false,
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
        },
      });

      if (!environment.production) {
        console.log('Initalising user tracking', {
          isFirstNewOrg,
          isTrackingInitialised: this.isTrackingInitialised,
          userEmail,
          userId,
        });
      }
    }
  }

  startSessionReplay() {
    if (!this.sessionReplayTracking) {
      return;
    }
    amplitude.add(this.sessionReplayTracking);
  }

  trackEvent(eventName: string, eventValue: string | null = null, data: any = null) {
    if (!environment.production && this.isNotTestOrganisation) {
      console.log('Tracking event', eventName, eventValue, data);
    }

    try {
      amplitude.track(eventName, { eventValue, ...data });
    } catch (e) {
      this.instrumentation.logError(e);
    }
  }

  updateIdentity(organisation: OrganisationDto | null) {
    this.organisation = organisation;

    if (
      this.isNotTestOrganisation &&
      organisation?.organisationId &&
      // Don't update identity if this is the first new organisation as it interrupts the session replay
      !this.isFirstNewOrg
    ) {
      try {
        amplitude.reset();

        this.subscriptionService
          .getSubscription(organisation?.organisationId)
          .pipe(take(1))
          .subscribe(subscription => {
            const identifyEvent = new amplitude.Identify();
            identifyEvent.set('OrganisationId', organisation?.organisationId ?? '');
            identifyEvent.set('OrganisationName', organisation?.name ?? '');
            identifyEvent.set('Package Name', subscription.packageName);
            identifyEvent.set('Is Managed', subscription.isManaged);

            amplitude.identify(identifyEvent);
          });

        if (!environment.production) {
          console.log('Updating identity', {
            organisationId: organisation?.organisationId,
            organisationName: organisation?.name,
          });
        }
      } catch (e) {
        this.instrumentation.logError(e);
      }
    }
  }

  private _referralSource?: string;
  trackMarketingSource(referralSource: string) {
    this._referralSource = referralSource;
  }

  getMarketingSource() {
    return this._referralSource;
  }
}
